import * as React from "react"
import { useEffect, useLayoutEffect } from "react"
import { Box, ButtonLink, EmptyState, EmptyStateTitle, Flex, Text } from "@indeed/ifl-components"
import { ArrowRight } from "@indeed/ifl-icons"
import { Rating1 } from "@indeed/indeed-design-system-illustrations-spot"
import { configSelector } from "../../../common/utils/config-slice"
import { useI18n } from "../../../common/utils/i18n-context"
import { useAppSelector } from "../../../common/utils/redux-helpers"
import { displayFooter } from "../../../common/utils/utils"
import { selectedFeedback, PreferenceSlashableIcon, toggledRequirementsSelector } from "../../feedback-form"

export const ExplicitFeedbackThanksPage: React.FC = () => {
    const selectedFbk = useAppSelector(selectedFeedback)
    const toggledRequirements = useAppSelector(toggledRequirementsSelector)
    const config = useAppSelector(configSelector)
    const shownFbk = toggledRequirements.concat(selectedFbk)
    const { gettext } = useI18n()

    useLayoutEffect(() => {
        displayFooter()
    }, [])

    useEffect(() => {
        document.title = gettext("Thanks")
        document.getElementById("explicit-feedback-thanks-header")?.focus()
    }, [])

    const preferencesPath = "/exclusions"
    const preferencesLink = `${config.profileUrl}${preferencesPath}`
    const preferencesLinkLabel = gettext("Manage profile")

    const feedbackMessage = shownFbk.length > 0 && gettext("We'll take these preferences into account:")

    return (
        <EmptyState
            illustration={<Rating1 size="sm" />}
            actions={[
                <ButtonLink href={config.jobSearchUrl} variant="secondary" key="secondary" iconAfter={<ArrowRight />}>
                    {gettext("Find jobs")}
                </ButtonLink>,
                <ButtonLink key="primary" iconAfter={<ArrowRight />} href={preferencesLink}>
                    {preferencesLinkLabel}
                </ButtonLink>,
            ]}
            sx={{
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginInlineEnd: "auto",
                marginInlineStart: "auto",
                maxInlineSize: "35.625rem",
                paddingBlockEnd: 2,
                paddingBlockStart: "2.72rem",
                paddingInlineEnd: 2,
                paddingInlineStart: 2,
                svg: {
                    maxInlineSize: "4.93rem !important",
                },
            }}
            id="explicit-feedback-thanks-page-main"
        >
            <EmptyStateTitle
                as="h1"
                sx={{
                    marginBlockStart: 3,
                    marginBlockEnd: 5,
                    paddingBlockEnd: 2,
                    ":focus-visible": { outline: "none" },
                }}
                // @ts-ignore
                id="explicit-feedback-thanks-header"
                tabIndex={-1}
            >
                {gettext("Thanks for your feedback.")}
                <br />
                {feedbackMessage}
            </EmptyStateTitle>
            <Flex
                as="ul"
                sx={{
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: 4,
                    marginBlockEnd: 5,
                    paddingInlineStart: 0,
                }}
            >
                {shownFbk.map((it) => {
                    return (
                        <Box
                            as="li"
                            data-testid="selected-feedback-chip"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "row",
                                gap: 2,
                                paddingBlockEnd: 2,
                                paddingBlockStart: 2,
                                paddingInlineEnd: 4,
                                paddingInlineStart: 4,
                                borderRadius: "lg",
                                border: "1px solid",
                                borderColor: "border.weak",
                            }}
                            key={it.field + it.value}
                        >
                            <PreferenceSlashableIcon category={it.iconCategory} isSlashed />
                            <Text level={2} sx={{ lineHeight: "tight" }}>
                                {it.field === "multiselect-location" ? it.value : it.unselectedText}
                            </Text>
                        </Box>
                    )
                })}
            </Flex>
            <Box
                sx={{
                    marginBlockEnd: 6,
                    textAlign: "center",
                }}
            >
                <Text level={2}>{gettext("Please allow up to 24 hours for preferences to appear in your profile.")}</Text>
            </Box>
        </EmptyState>
    )
}
